import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  pluralize,
  singularize,
} from '@/features/enterprise/components/SelectCheckboxIntegracao/helpers';
import { groupBy } from '@/utils/collections';

import { useIntegrationContext } from '@/features/enterprise/contexts/IntegrationContext';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';

import BoxCondutaComplementar from '@/features/enterprise/components/SelectCheckboxIntegracao/BoxCondutaComplementar';
import OptionCheckboxItem from '@/features/enterprise/components/SelectCheckboxIntegracao/Item';
import SelectCheckbox from '@/features/enterprise/components/SelectCheckboxIntegracao/SelectCheckbox';
import CategoriaArtefato from '@/features/execucao/components/CategoriaArtefato';
import Feature from '@/features/features/components/Feature';
import OptionIntegracao from '@/types/OptionIntegracao';

import { Container, Content } from './styles';

interface SelectListProps {
  options: OptionIntegracao[];
  generalStatus: string;
  totalSolicitados?: number;
  type?: 'Medicamento';
  onChange?: (
    selectedOptions: OptionIntegracao[],
    enabledOptions?: OptionIntegracao[],
  ) => void;
}

const SelectList: React.FC<SelectListProps> = ({
  type = 'Medicamento',
  options,
  generalStatus,
  totalSolicitados,
  onChange,
}: SelectListProps) => {
  const [selectOptions, setSelectOptions] = useState<OptionIntegracao[]>([]);

  const [{ generalStatus: globalStatus }] = useIntegrationContext();

  const [{ isProtocoloReadOnly }] = useExecucaoContext();

  const disabled = useMemo(
    () => generalStatus === 'done' || generalStatus === 'disabled',
    [generalStatus],
  );

  const disableClick = useMemo(
    () =>
      generalStatus === 'done' ||
      generalStatus === 'disabled' ||
      generalStatus === 'loading',
    [generalStatus],
  );

  const checkboxState = useMemo((): 'unmarked' | 'marked' | 'half-marked' => {
    if (
      generalStatus === 'all_selected' ||
      generalStatus === 'done' ||
      generalStatus === 'disabled'
    )
      return 'marked';
    if (generalStatus === 'some_selected') return 'half-marked';

    return 'unmarked';
  }, [generalStatus]);

  const titleText = useMemo(() => {
    if (generalStatus === 'done' && totalSolicitados) {
      if (options.length === totalSolicitados) {
        return totalSolicitados > 1 ? pluralize[type] : singularize[type];
      }

      return `${totalSolicitados} ${
        totalSolicitados > 1 ? pluralize[type] : singularize[type]
      }`;
    }

    return 'Selecionar todos';
  }, [generalStatus, options, totalSolicitados, type]);

  const condutaComplementarFeatureName = React.useMemo(() => {
    return `CONDUTA_COMPLEMENTAR__${type.toUpperCase()}`;
  }, [type]);

  const artefatosPossuemCategorias = useMemo(() => {
    return options.filter(option => !!option.categoria).length > 0;
  }, [options]);

  const hasFhirRequest = useCallback(
    (option: OptionIntegracao) => !!option.pedido,
    [],
  );

  const handleOptionSelection = useCallback(
    (option: OptionIntegracao) => {
      if (disableClick || !!option.status) return;
      let newOptions = selectOptions;

      if (selectOptions.includes(option)) {
        newOptions = selectOptions.map(opt => {
          if (opt.key === option.key) {
            return { ...opt, selected: !opt.selected };
          }

          return opt;
        });
      } else {
        newOptions = [...selectOptions, option];
      }

      setSelectOptions(newOptions);

      if (onChange) {
        onChange(
          newOptions.filter(opt => opt.selected),
          newOptions,
        );
      }
    },
    [disableClick, selectOptions, onChange],
  );

  const handleAllOptionsSelection = useCallback(() => {
    if (disableClick) return;

    const newOptions = selectOptions.map(opt => ({
      ...opt,
      selected: hasFhirRequest(opt) && generalStatus === 'none_selected',
    }));

    setSelectOptions(newOptions);

    if (onChange) {
      onChange(
        newOptions.filter(opt => opt.selected),
        newOptions,
      );
    }
  }, [disableClick, generalStatus, selectOptions, onChange, hasFhirRequest]);

  useEffect(() => {
    if (globalStatus === 'none_selected') {
      const newOptions = selectOptions.map(opt => ({
        ...opt,
        selected: false,
      }));
      setSelectOptions(newOptions);

      if (onChange) {
        onChange(newOptions.filter(opt => opt.selected));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, globalStatus, onChange]);

  useEffect(() => {
    const innerOptions = options.map((opt, index) => ({
      ...opt,
      key: opt.value.toString() || index.toString(),
      selected: isProtocoloReadOnly
        ? opt.selected
        : opt.selected ||
          (opt.pedido.status === 'draft' && opt.selectedDefault),
    }));

    setSelectOptions(innerOptions);

    if (onChange) {
      onChange(innerOptions.filter(opt => opt.selected));
    }
  }, [isProtocoloReadOnly, onChange, options]);

  const CategoriaContent = React.useMemo(() => {
    const groupedArtefatos = groupBy(selectOptions, option =>
      option.pedido.intent === 'order'
        ? 'Itens complementares'
        : option.categoria || 'Outros',
    );

    const artefatosEntries = Object.entries(groupedArtefatos);

    return artefatosEntries.map(([key, value], idx) => (
      <CategoriaArtefato
        key={key}
        name={key}
        priority={artefatosEntries.length - idx}
        intialConfig="expanded"
        totalSelected={value.filter(item => item.selected).length}
        artefatos={value.map((opt, index) => (
          <OptionCheckboxItem
            key={opt.key || index.toString()}
            type={type}
            option={opt}
            selected={!!opt.selected}
            disabled={
              opt.status === 'solicitado' || !hasFhirRequest(opt) || disabled
            }
            included={!!opt.included}
            loading={!!opt.selected && generalStatus === 'loading'}
            error={generalStatus === 'error'}
            handleClick={handleOptionSelection}
          />
        ))}
      />
    ));
  }, [
    type,
    disabled,
    generalStatus,
    handleOptionSelection,
    hasFhirRequest,
    selectOptions,
  ]);

  return (
    <Container>
      {artefatosPossuemCategorias ? (
        <Content>{CategoriaContent}</Content>
      ) : (
        <Content>
          {!isProtocoloReadOnly && (
            <SelectCheckbox
              type={type}
              disabled={disabled}
              generalStatus={generalStatus}
              titleText={titleText}
              checkboxState={checkboxState}
              handleAllOptionsSelection={handleAllOptionsSelection}
            />
          )}
          {selectOptions.map((opt, index) => (
            <OptionCheckboxItem
              key={opt.key || index.toString()}
              type={type}
              option={opt}
              selected={!!opt.selected}
              disabled={
                opt.status === 'solicitado' || !hasFhirRequest(opt) || disabled
              }
              included={!!opt.included}
              error={generalStatus === 'error'}
              loading={!!opt.selected && generalStatus === 'loading'}
              handleClick={handleOptionSelection}
            />
          ))}
        </Content>
      )}

      {!disabled && (
        <Feature name={condutaComplementarFeatureName}>
          <BoxCondutaComplementar
            type={type}
            disabled={disabled}
            handleOptionConfirm={handleOptionSelection}
          />
        </Feature>
      )}
    </Container>
  );
};

SelectList.defaultProps = {
  onChange: undefined,
};

export default SelectList;
